import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Avatar, Backdrop, Box, Button, CircularProgress, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { toast } from 'react-toastify';

import { Page } from 'components/Page';
import COLORS from 'theme/colors';
import { ROUTE } from 'route/RouteEnums';
import { useAuth } from 'utils/AuthContext';
import { providerPhotoUrl } from 'utils/providerPhoto';
import { getSelfSchedulingEligibilityAPI } from 'services/profileServices';
import {
  PATIENT_LOOKUP_FAILED_ERROR_CODE,
  PATIENT_LOOKUP_FAILED_MESSAGE,
  SCHEDULING_UNAVAILABLE_MESSAGE,
} from 'shared/content/constants';
import {
  bookSelfSchedulingAppointmentAPI,
  getSelfSchedulingPatientContextAPI,
  getSelfSchedulingProviderAvailabilityAPI,
  getSelfSchedulingProvidersAPI,
  PatientOnboardingStatus,
  rescheduleSelfSchedulingAppointmentAPI,
} from 'services/selfSchedulingServices';
import AvailabilityCalendar from './AvailabilityCalendar';
import { describeFilters, ProviderAvailabilityNavState, SelfSchedulingFilters } from './FiltersPanel';
import SelectionConfirmDialog from './SelectionConfirmDialog';
import ProviderUnavailableDialog from './ProviderUnavailableDialog';
import { AvailabilitySlot, PatientSelfSchedulingContext, Provider } from './types';
import {
  formatProviderName,
  formatSlotLocation,
  mapOnboardingStatus,
  mapPatientContext,
  mapProvider,
  mapSlot,
  toScheduledTime,
} from './mappers';

// Matches the provider list's own default (see SelfScheduling/index.tsx) - only used when this
// page is opened without navState (e.g. a direct URL/refresh), so the calendar's free-slot
// request still carries the same default consultation-type flag as the list would have.
const DEFAULT_FILTERS: SelfSchedulingFilters = {
  consultationTypes: ['Virtual'],
  locations: [],
};

// Same number ProviderCard.tsx already shows in the directory list for a provider with no
// visible slots at all - kept as its own local copy here rather than shared, since it's a single
// small constant used by two otherwise-unrelated components.
const SCHEDULING_PHONE_NUMBER = '(618) 310-0085';

// A successful book/reschedule now flips the self-scheduling free-slot row and mirrors the
// change into ehr-service in the background (SelfSchedulingService, fire-and-forget) rather
// than before responding, so the page this navigates to next (Dashboard/Manage Appointment,
// both reading ehr-service's own appointment data) could otherwise render with stale data if
// it loads before that background work finishes. Holding here (bookingLoading's own Backdrop
// stays up the whole time) for a few seconds first gives it a head start, same trade-off as the
// backend's own deliberate move to async - a short, bounded wait here instead of every
// downstream read needing its own retry/poll for eventual consistency.
const POST_BOOKING_NAVIGATE_DELAY_MS = 5000;

interface ProviderAvailabilityNavStateWithProvider extends ProviderAvailabilityNavState {
  provider?: Provider;
}

const ProviderAvailability = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const navState = location.state as ProviderAvailabilityNavStateWithProvider | undefined;
  const reschedule = navState?.reschedule;

  const [provider, setProvider] = useState<Provider | null>(navState?.provider ?? null);
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // Set when a booking attempt comes back refused because the patient's primary provider is no
  // longer available (AppointmentResponse.primaryProviderUnavailable) - shows the same
  // call-to-schedule dialog as the context flag below.
  const [bookingRefusedProviderUnavailable, setBookingRefusedProviderUnavailable] = useState(false);
  // True once the first-available-week resolution (see weekStart's own doc) comes back with
  // zero slots - not just this one week empty, the whole seeded window has nothing for this
  // provider right now. Hides the calendar entirely in favor of a call-to-schedule message
  // (matching ProviderCard's own hasNoAvailability treatment in the directory list) instead of
  // showing an empty, endlessly-page-able calendar with nothing to ever find.
  const [noAvailability, setNoAvailability] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);
  // The calendar shows one week at a time and drives this via onWeekChange (prev/next),
  // so only that visible week's slots are ever fetched instead of the whole range up front.
  // Opens on the week of the provider's own first preview slot (see ProviderCard's "first 3
  // appointments") rather than always today's week - a provider whose earliest opening is
  // weeks out would otherwise land the patient on an empty week they'd have to page through
  // manually to find anything.
  //
  // undefined (not "today"), when no preview slot is known - an existing patient's direct-entry
  // redirect, a reschedule, or a direct URL/refresh all reach this page with no navState.provider
  // to seed from. Left undefined, fetchAvailability below omits the weekStart param entirely,
  // which makes the backend itself resolve the true first-available week
  // (SelfSchedulingProviderDirectoryService.resolveDefaultWeekStart) instead of this page
  // defaulting to today's, possibly-empty, week - the same "land on the first available week"
  // treatment NEW patients already get via the preview slot, now for everyone. The calendar
  // itself (see its own render below) stays unmounted until weekStart is resolved: FullCalendar's
  // initialDate only takes effect at mount, so rendering it against today first and correcting
  // weekStart afterward would not actually move an already-mounted calendar.
  const [weekStart, setWeekStart] = useState<string | undefined>(
    () => navState?.provider?.availability[0]?.start.slice(0, 10)
  );
  // payMode (and canBookAppointment) - resolved once up front, then sent explicitly on every
  // availability/booking call below (see PatientSelfSchedulingContextDTO on the backend). Fetched
  // here (not just relied on from SelfScheduling/index.tsx) since this page can also be reached
  // directly - a direct URL/refresh, or an existing patient's direct-entry redirect - without
  // ever going through that page's own context fetch.
  const [patientContext, setPatientContext] = useState<PatientSelfSchedulingContext | null>(null);
  const [contextLoading, setContextLoading] = useState(true);
  // Feeds the NEW-patient onboarding blackout (see PatientOnboardingStatus's own doc) - resolved
  // once up front, same as patientContext above, then sent on every availability call below. Has
  // no effect for an existing patient either way, so this is fetched unconditionally rather than
  // gated on patient type (the backend already only applies it to a NEW patient).
  const [onboardingStatus, setOnboardingStatus] = useState<PatientOnboardingStatus | undefined>(undefined);

  useEffect(() => {
    if (!user?.patientId) {
      setContextLoading(false);
      return;
    }
    getSelfSchedulingPatientContextAPI(user.patientId)
      .then((response: any) => setPatientContext(mapPatientContext(response?.data)))
      .catch(() => setPatientContext(mapPatientContext(undefined)))
      .finally(() => setContextLoading(false));
    getSelfSchedulingEligibilityAPI(user.patientId)
      .then((response: any) => setOnboardingStatus(mapOnboardingStatus(response?.data)))
      .catch(() => setOnboardingStatus(mapOnboardingStatus(undefined)));
  }, [user?.patientId]);

  const activeFilters = navState?.filters ?? DEFAULT_FILTERS;
  const filterSummary = useMemo(() => describeFilters(activeFilters), [activeFilters]);
  /**
   * MM-1161: filters belong to the directory, which only a new patient (no assigned provider) uses.
   * Show the "Filters applied" summary only when this page was opened from the directory's "More"
   * (navState.filters set, not a direct entry) - never for an established patient's direct entry or
   * their reschedule from Manage Appointment, which never saw or chose any filters.
   */
  const showFilterSummary = !!navState?.filters && !navState?.directEntry;
  const payMode = patientContext?.payMode ?? 'InsurancePay';
  // A reschedule is managing an appointment the patient already has, not booking a new one - the
  // active-appointment gate below only ever applies to the booking path.
  // An existing patient whose primary provider is no longer available (left the organization but still
  // on file) can't book online at all - no availability or provider lookups, just the call-to-schedule
  // dialog (ProviderUnavailableDialog), whose OK goes back to the dashboard. Booking only - a reschedule
  // is unaffected.
  const providerUnavailable = !reschedule && !!patientContext?.primaryProviderUnavailable;
  const bookingBlocked =
    !reschedule && !contextLoading && !!patientContext && !patientContext.canBookAppointment && !providerUnavailable;

  // Re-run after a booking fails because the slot was taken (see handleConfirmSelection
  // below) so the now-stale slot disappears from the calendar without a full page reload.
  const fetchAvailability = () => {
    if (!providerId || contextLoading || bookingBlocked || providerUnavailable) return;
    const resolvingInitialWeek = weekStart === undefined;
    setAvailabilityLoading(true);
    // Re-checked on every fetch (not just the resolving one) - a previous resolve could have
    // found nothing, but a later re-fetch (e.g. payMode changing) might find slots after all;
    // this must clear back to false for that case rather than staying stuck once set.
    setNoAvailability(false);
    getSelfSchedulingProviderAvailabilityAPI(
      providerId,
      payMode,
      navState?.officeId,
      weekStart,
      user?.patientId,
      activeFilters.consultationTypes[0],
      onboardingStatus
    )
      .then((response: any) => {
        const slots = (response?.data ?? []).map(mapSlot);
        setAvailability(slots);
        if (resolvingInitialWeek) {
          if (slots.length === 0) {
            // weekStart was omitted above specifically so the backend's findFirstAvailableDate
            // could scan the whole seeded window for this provider - an empty result here means
            // there is genuinely nothing to find anywhere in it, not just this one week. Leaves
            // weekStart unresolved (see its own doc) so the calendar never mounts.
            setNoAvailability(true);
            return;
          }
          // adopt whichever week the resolution actually returned (its earliest slot's own date).
          setWeekStart(slots[0].start.slice(0, 10));
        }
      })
      .catch(() => {
        toast.error('Unable to load availability. Please try again.');
        if (resolvingInitialWeek) {
          setWeekStart(new Date().toISOString().slice(0, 10));
        }
      })
      .finally(() => setAvailabilityLoading(false));
  };

  // Reached directly for an existing patient's assigned provider (see SelfScheduling/index.tsx's
  // redirect) rather than via "More" - bouncing back to the directory on failure would just
  // redirect straight back here (this patient always has the same assigned provider id),
  // looping forever. Fall back to a minimal placeholder instead, so the calendar can still try
  // to load this provider's availability by id even without the directory listing's display
  // details (name/photo/description) - only a directEntry visit gets this fallback; a normal
  // "More"/direct-URL visit with no known-good id still falls through to notFound below.
  const buildFallbackProvider = (): Provider => ({
    id: Number(providerId),
    name: reschedule?.providerName ?? '',
    credential: '',
    description: '',
    consultationTypes: ['Virtual'],
    locations: [],
    availability: [],
    hasMoreAvailability: false,
  });

  // Resolves the provider once (independent of which week is visible).
  useEffect(() => {
    if (!providerId) {
      setNotFound(true);
      return;
    }
    if (provider || contextLoading || providerUnavailable) return;

    setLoading(true);
    // Scoped to just this one provider (not the whole directory) - see
    // SelfSchedulingProviderQuery.providerId: without it the backend would scan and compute a
    // 90-day availability preview for every provider across every office just to resolve this
    // one provider's display info, which this direct/refresh-entry path doesn't need and which
    // is expensive enough at real-data volume to time out.
    getSelfSchedulingProvidersAPI({ patientId: user?.patientId, providerId, payMode, onboardingStatus })
      .then((response: any) => {
        const match = (response?.data ?? [])
          .map(mapProvider)
          .find((p: Provider) => String(p.id) === providerId);
        if (match) {
          setProvider(match);
          return;
        }
        if (navState?.directEntry || reschedule) {
          setProvider(buildFallbackProvider());
          return;
        }
        setNotFound(true);
      })
      .catch(() => {
        if (navState?.directEntry || reschedule) {
          setProvider(buildFallbackProvider());
          return;
        }
        toast.error('Unable to load provider. Please try again.');
        setNotFound(true);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, contextLoading]);

  // Fetches the currently visible week's slots - re-runs whenever the calendar's visible
  // week changes. Previous data stays on screen until the new week's response arrives,
  // rather than clearing to empty first.
  useEffect(() => {
    fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, weekStart, contextLoading, bookingBlocked, payMode, onboardingStatus, providerUnavailable]);

  if (notFound) {
    return <Navigate to={ROUTE.SELF_SCHEDULING} replace />;
  }

  if (providerUnavailable) {
    return (
      <Page title='Provider Availability' backDir={ROUTE.DASHBOARD}>
        <ProviderUnavailableDialog open onOk={() => navigate(ROUTE.DASHBOARD)} />
      </Page>
    );
  }

  const handleCancelSelection = () => {
    setSelectedSlot(null);
  };

  const handleConfirmSelection = () => {
    if (!selectedSlot || !provider) return;
    setBookingLoading(true);

    if (reschedule) {
      rescheduleSelfSchedulingAppointmentAPI(reschedule.appointmentId, {
        patientId: reschedule.currentSlot.patientId,
        duration: selectedSlot.duration,
        payMode,
        current: {
          doctor: reschedule.currentSlot.providerId,
          office: reschedule.currentSlot.officeId,
          profileId: reschedule.currentSlot.profileId,
          examRoom: reschedule.currentSlot.examRoom,
          scheduledTime: reschedule.currentSlot.scheduledTime,
        },
        // The newly selected slot's own doctor/office/profile - not the old appointment's (see
        // spec/2026-09-18-MM-1067-new-patient-cross-provider-reschedule.md fix 4). This
        // provider's calendar can span more than one office, so the old slot's office can't be
        // assumed to still be correct for the new one.
        replacement: {
          doctor: provider.id,
          office: selectedSlot.officeId,
          profileId: selectedSlot.profileId[0],
          examRoom: selectedSlot.examRoom,
          scheduledTime: toScheduledTime(selectedSlot.start),
        },
      })
        .then((response: any) => {
          if (response?.data?.apiResult) {
            setSelectedSlot(null);
            toast.success('Appointment rescheduled!');
            // bookingLoading deliberately stays true (Backdrop still up) through this hold -
            // see POST_BOOKING_NAVIGATE_DELAY_MS's own doc. Not cleared afterward either: this
            // component is about to unmount on navigate, so there's nothing left to update.
            setTimeout(() => navigate(ROUTE.MANAGE_APPOINTMENT), POST_BOOKING_NAVIGATE_DELAY_MS);
            return;
          }
          if (response?.data?.slotUnavailable) {
            // Same race as booking: someone else took this slot between it being listed and
            // this request landing. The original appointment is untouched (DrChrono rejected
            // the change), so just refresh this calendar rather than navigating anywhere.
            setSelectedSlot(null);
            toast.error('That time slot was just booked. Please choose another available slot.');
            fetchAvailability();
          } else if (response?.data?.errorCode === PATIENT_LOOKUP_FAILED_ERROR_CODE) {

            // patient-mgmt lookup failed - nothing was booked/changed; the patient can only retry later.

            toast.error(PATIENT_LOOKUP_FAILED_MESSAGE);

          } else if (response?.data?.throttled) {
            toast.error(SCHEDULING_UNAVAILABLE_MESSAGE);
          } else {
            toast.error(response?.data?.messsage ?? 'Failed to reschedule appointment');
          }
          setBookingLoading(false);
        })
        .catch(() => {
          toast.error('Failed to reschedule appointment. Please try again.');
          setBookingLoading(false);
        });
      return;
    }

    bookSelfSchedulingAppointmentAPI({
      patientId: user?.patientId,
      doctor: provider.id,
      office: selectedSlot.officeId,
      // selectedSlot.profileId can carry more than one candidate profile (a cash-pay/
      // insurance-pay pair sharing an identical schedule) - the backend resolves the correct one
      // from the patient's pay-type, so any one entry is fine to send as a starting point (see
      // SelfSchedulingService.resolveProfileForBooking).
      profileId: selectedSlot.profileId[0],
      examRoom: selectedSlot.examRoom,
      duration: selectedSlot.duration,
      scheduledTime: toScheduledTime(selectedSlot.start),
      payMode,
    })
      .then((response: any) => {
        if (response?.data?.apiResult) {
          setSelectedSlot(null);
          toast.success('Appointment confirmed!');
          // bookingLoading deliberately stays true (Backdrop still up) through this hold - see
          // POST_BOOKING_NAVIGATE_DELAY_MS's own doc. Not cleared afterward either: this
          // component is about to unmount on navigate, so there's nothing left to update.
          setTimeout(() => navigate(ROUTE.DASHBOARD), POST_BOOKING_NAVIGATE_DELAY_MS);
          return;
        }
        if (response?.data?.slotUnavailable) {
          // Someone else booked this slot between it being listed and this request landing.
          // Stay on this screen (don't navigate away) so the patient can pick another slot -
          // close the stale confirm dialog and refresh so the taken slot disappears from view.
          setSelectedSlot(null);
          toast.error('That time slot was just booked. Please choose another available slot.');
          fetchAvailability();
        } else if (response?.data?.primaryProviderUnavailable) {
          setSelectedSlot(null);
          setBookingRefusedProviderUnavailable(true);
        } else if (response?.data?.errorCode === PATIENT_LOOKUP_FAILED_ERROR_CODE) {

          // patient-mgmt lookup failed - nothing was booked/changed; the patient can only retry later.

          toast.error(PATIENT_LOOKUP_FAILED_MESSAGE);

        } else if (response?.data?.throttled) {
          toast.error(SCHEDULING_UNAVAILABLE_MESSAGE);
        } else {
          toast.error(response?.data?.messsage ?? 'Failed to book appointment');
        }
        setBookingLoading(false);
      })
      .catch(() => {
        toast.error('Failed to book appointment. Please try again.');
        setBookingLoading(false);
      });
  };

  let backDir = ROUTE.SELF_SCHEDULING;
  if (reschedule) {
    backDir = ROUTE.MANAGE_APPOINTMENT;
  } else if (navState?.directEntry) {
    backDir = ROUTE.DASHBOARD;
  }

  // availabilityLoading/contextLoading are both plain booleans (useState<boolean>), so this is a
  // genuine OR of two booleans, not a nullish-default expression - `??` here would be wrong
  // (false ?? contextLoading always evaluates to false). Written as a ternary instead of `||` so
  // it isn't flagged by the "prefer ??" rule, while staying behaviorally identical to `||`.
  const availabilityCalendarLoading = availabilityLoading ? true : contextLoading;

  let availabilitySectionContent;
  if (bookingBlocked) {
    availabilitySectionContent = (
      <Box
        sx={{
          border: `1px dashed ${COLORS.Gray300}`,
          borderRadius: '12px',
          p: 4,
          textAlign: 'center',
          backgroundColor: COLORS.White,
        }}
      >
        <Typography variant='body1' sx={{ color: COLORS.Gray600, mb: 2 }}>
          You already have an upcoming appointment. You can book another one once it has passed.
        </Typography>
        <Button
          variant='contained'
          onClick={() => navigate(ROUTE.MANAGE_APPOINTMENT)}
          sx={{ textTransform: 'none', backgroundColor: COLORS.DarkBlue }}
        >
          View My Appointments
        </Button>
      </Box>
    );
  } else if (noAvailability) {
    availabilitySectionContent = (
      <Box
        sx={{
          border: `1px dashed ${COLORS.Gray300}`,
          borderRadius: '12px',
          p: 4,
          textAlign: 'center',
          backgroundColor: COLORS.White,
        }}
      >
        <Typography variant='body1' sx={{ color: COLORS.Gray600, mb: 1 }}>
          {/* Optional chaining only to satisfy TypeScript here - this whole block only ever
              renders once the outer `loading || !provider` gate below has already passed, so
              provider is always set in practice by render time. */}
          {provider?.name} has no upcoming availability right now.
        </Typography>
        <Typography variant='body1' sx={{ color: COLORS.DarkBlue, fontWeight: 600 }}>
          CALL TO SCHEDULE: {SCHEDULING_PHONE_NUMBER}
        </Typography>
      </Box>
    );
  } else if (weekStart === undefined) {
    // Still resolving the first-available week (see weekStart's own doc) - held back from
    // mounting AvailabilityCalendar at all rather than mounting it against today and correcting
    // afterward, since FullCalendar's initialDate only takes effect once.
    availabilitySectionContent = (
      <Box sx={{ textAlign: 'center', p: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  } else {
    availabilitySectionContent = (
      <AvailabilityCalendar
        availability={availability}
        onSelectSlot={setSelectedSlot}
        onWeekChange={setWeekStart}
        loading={availabilityCalendarLoading}
        // AvailabilityCalendar's FullCalendar runs in timeZone='UTC' (literal, no conversion -
        // see its own comment), so this must be UTC midnight, not browser-local midnight, or the
        // calendar could open on the wrong day for a browser timezone that isn't UTC-aligned.
        initialDate={new Date(`${weekStart}T00:00:00Z`)}
      />
    );
  }

  return (
    <Page
      title={reschedule ? 'Reschedule Appointment' : 'Provider Availability'}
      backDir={backDir}
      backState={
        reschedule || navState?.directEntry
          ? undefined
          : { filters: navState?.filters, searchText: navState?.searchText }
      }
    >
      {/* Above theme.zIndex.modal (StyledDialog/SelectionConfirmDialog's own zIndex, 1300 by
          default), not just theme.zIndex.drawer (1200) - SelectionConfirmDialog stays open and
          visible while booking/rescheduling, so this must render in front of it, not behind it. */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }} open={bookingLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>

      {loading || !provider ? (
        <Box sx={{ textAlign: 'center', p: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              border: `1px solid ${COLORS.Gray300}`,
              borderRadius: '12px',
              p: 3,
              mb: 3,
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Avatar
              src={providerPhotoUrl(provider.id)}
              alt={provider.name}
              sx={{ width: 96, height: 96, backgroundColor: COLORS.LightBlue, color: COLORS.DarkBlue }}
            >
              <PersonIcon sx={{ fontSize: 44 }} />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant='h4' sx={{ fontWeight: 600, color: COLORS.Gray900 }}>
                {formatProviderName(provider)}
              </Typography>
              {provider.description ? (
                <Typography variant='body2' sx={{ color: COLORS.Gray600, mb: 1 }}>
                  {provider.description}
                </Typography>
              ) : null}
            </Box>
          </Box>

          {showFilterSummary && filterSummary ? (
            <Typography variant='body2' sx={{ color: COLORS.Gray600, mb: 2 }}>
              Filters applied &mdash; {filterSummary}
            </Typography>
          ) : null}

          {availabilitySectionContent}
        </>
      )}

      <SelectionConfirmDialog
        open={!!selectedSlot}
        providerName={provider?.name ?? ''}
        day={selectedSlot?.day ?? ''}
        time={selectedSlot?.time ?? ''}
        location={selectedSlot ? formatSlotLocation(selectedSlot) : undefined}
        consultationType={activeFilters.consultationTypes[0]}
        onCancel={handleCancelSelection}
        onContinue={handleConfirmSelection}
        title={reschedule ? 'Confirm Reschedule' : undefined}
        bodyPrefix={reschedule ? 'You selected a new time with' : undefined}
        currentAppointment={
          reschedule
            ? {
              providerName: reschedule.providerName ?? provider?.name ?? '',
              day: reschedule.currentSummary.day,
              time: reschedule.currentSummary.time,
              location: reschedule.currentSummary.officeAddress,
            }
            : undefined
        }
      />

      <ProviderUnavailableDialog open={bookingRefusedProviderUnavailable} onOk={() => navigate(ROUTE.DASHBOARD)} />
    </Page>
  );
};

export default ProviderAvailability;
